/** Editable per-channel delivery settings (destinations, recipients, channel
 *  extras) for the selected delivery method — dashboard parity for the
 *  show/hide rules (see dashboard DeliveryMethodEdit.tsx):
 *
 *  - email/sms/whatsapp: destination select; support-contact picker when
 *    destination=1 (tenant-scoped — disabled with a hint on system templates);
 *    arbitrary address/number input when destination=2; allow-override flag;
 *    email adds reply-to, append-conversation-sid and the five attach flags.
 *  - chat: destination (+ conversation SID when arbitrary), origin (+ origin
 *    user picker when origin=User).
 *  - airbnb: destination (+ thread id when arbitrary).
 *  - non-chat messaging channels: prevent replies (+ auto-response message).
 *  - http: method/url/expected status/retry count + headers/data JSON.
 *  - sns: topic + AWS keys (SENTINEL-AWARE: '•••' = unchanged — never shows or
 *    resends a real secret).
 *  - sentry: tag key/value, context JSON, extra.
 *
 *  All writes go through the page draft via `onFieldChange` (setDmField).
 */
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import type { NotificationDeliveryMethod, SupportContact } from '@/api/notifications';
import { DELIVERY_METHODS, fetchSupportContacts } from '@/api/notifications';
import * as usersApi from '@/api/users';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { JsonEditorWidget, type JsonEditorHandle } from '@/components/JsonEditor/JsonEditorWidget';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

import { DM_FIELD_HELP } from '../fieldHelp';
import { userActions } from '../notificationLinks';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';

import {
  AIRBNB_DESTINATION_LABELS,
  AIRBNB_TO_ARBITRARY_THREAD,
  CHAT_DESTINATION_LABELS,
  CHAT_ORIGIN_LABELS,
  CHAT_ORIGIN_USER,
  CHAT_TO_ARBITRARY_SID,
  DEST_ARBITRARY,
  DEST_SUPPORT_CONTACT,
  DESTINATION_CHANNELS,
  EMAIL_ATTACH_FIELDS,
  HTTP_METHODS,
} from './channelFields';

// ---------------------------------------------------------------------------
// Value helpers — NDM rows carry `unknown` per field; new (negative-id) rows
// hold nulls, which display as the backend's model defaults.
// ---------------------------------------------------------------------------

function numOr(dm: NotificationDeliveryMethod, field: string, fallback: number): number {
  const v = dm[field];
  return typeof v === 'number' ? v : fallback;
}

function strOf(dm: NotificationDeliveryMethod, field: string): string {
  const v = dm[field];
  return typeof v === 'string' ? v : '';
}

function boolOf(dm: NotificationDeliveryMethod, field: string): boolean {
  return Boolean(dm[field]);
}

function idOf(dm: NotificationDeliveryMethod, field: string): number | null {
  const v = dm[field];
  return typeof v === 'number' ? v : null;
}

interface FieldProps {
  dm: NotificationDeliveryMethod;
  canManage: boolean;
  onFieldChange: (dmId: number, field: string, value: unknown) => void;
}

interface PanelProps extends FieldProps {
  /** The notification's tenant (null = system template — no tenant lookups). */
  tenantId: number | null;
}

// ---------------------------------------------------------------------------
// Shared field widgets
// ---------------------------------------------------------------------------

function EnumSelect({
  dm,
  canManage,
  onFieldChange,
  field,
  label,
  labels,
  defaultValue = 0,
}: FieldProps & {
  field: string;
  label: string;
  labels: Record<number, string>;
  defaultValue?: number;
}) {
  const help = DM_FIELD_HELP[field];
  const select = (
    <TextField
      select
      size="small"
      sx={{ width: 280 }}
      label={label}
      value={numOr(dm, field, defaultValue)}
      disabled={!canManage}
      onChange={(e) => onFieldChange(dm.id, field, Number(e.target.value))}
    >
      {Object.entries(labels).map(([value, text]) => (
        <MenuItem key={value} value={Number(value)}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
  if (!help) return select;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {select}
      <FieldHelp help={help} />
    </Box>
  );
}

/** Quick actions for a settings field's CURRENT value — a mail/phone/URL the
 *  operator will want to open or copy. The input keeps its primary click and
 *  its typing; the actions live in a small end adornment, and an empty value
 *  yields none so a blank field never grows a dead trigger. */
function ValueAdornment({ actions }: { actions: ValueAction[] }) {
  if (actions.length === 0) return null;
  return (
    <ValueActions chip actions={actions} sx={{ color: 'text.secondary', lineHeight: 0, mr: 0.5 }}>
      <LinkIcon fontSize="small" titleAccess="Value actions" />
    </ValueActions>
  );
}

/** An email address / phone number / http URL, as its own actions. */
function contactActions(value: string, kind: 'email' | 'tel' | 'url'): ValueAction[] {
  const v = value.trim();
  if (!v) return [];
  if (kind === 'email') {
    return /^[^@\s]+@[^@\s]+$/.test(v) ? [act.mailto(v), act.copy(v, 'Copy address')] : [act.copy(v, 'Copy value')];
  }
  if (kind === 'tel') {
    return /^\+?[\d ()-]{5,}$/.test(v) ? [act.tel(v), act.copy(v, 'Copy number')] : [act.copy(v, 'Copy value')];
  }
  return /^https?:\/\/\S+$/i.test(v)
    ? [act.external(v, 'Open the URL'), act.copy(v, 'Copy URL')]
    : [act.copy(v, 'Copy value')];
}

function TextInput({
  dm,
  canManage,
  onFieldChange,
  field,
  label,
  width = 280,
  multiline = false,
  type,
  helperText,
  actionKind,
}: FieldProps & {
  field: string;
  label: string;
  width?: number | string;
  multiline?: boolean;
  type?: string;
  helperText?: string;
  /** Offer this value's own quick actions in an end adornment. */
  actionKind?: 'email' | 'tel' | 'url' | 'copy';
}) {
  const help = DM_FIELD_HELP[field];
  const value = strOf(dm, field);
  const actions: ValueAction[] = !actionKind
    ? []
    : actionKind === 'copy'
      ? value.trim()
        ? [act.copy(value.trim(), `Copy ${label.toLowerCase()}`)]
        : []
      : contactActions(value, actionKind);
  const adornment =
    actions.length > 0 || help ? (
      <InputAdornment position="end">
        <ValueAdornment actions={actions} />
        {help && <FieldHelp help={help} />}
      </InputAdornment>
    ) : null;
  return (
    <TextField
      size="small"
      sx={{ width }}
      label={label}
      value={value}
      disabled={!canManage}
      multiline={multiline}
      {...(multiline ? { minRows: 2 } : {})}
      {...(type ? { type } : {})}
      {...(helperText ? { helperText } : {})}
      {...(adornment ? { InputProps: { endAdornment: adornment } } : {})}
      onChange={(e) => onFieldChange(dm.id, field, e.target.value)}
    />
  );
}

function FlagSwitch({
  dm,
  canManage,
  onFieldChange,
  field,
  label,
  hint,
}: FieldProps & { field: string; label: string; hint?: string }) {
  const help = DM_FIELD_HELP[field];
  const labelNode = <Typography variant="body2">{label}</Typography>;
  const control = (
    <FormControlLabel
      sx={{ mr: 1 }}
      control={
        <Switch
          size="small"
          checked={boolOf(dm, field)}
          disabled={!canManage}
          onChange={(e) => onFieldChange(dm.id, field, e.target.checked)}
        />
      }
      label={help ? labelWithHelp(labelNode, help) : labelNode}
    />
  );
  return hint ? <Tooltip title={hint}>{control}</Tooltip> : control;
}

/** Support-contact picker — tenant-scoped (the endpoint requires a tenant, so
 *  it is disabled with a hint on system templates). The set is small (max 100
 *  rows), loaded once and filtered locally. */
function SupportContactField({
  dm,
  tenantId,
  canManage,
  onFieldChange,
  field,
  label,
}: PanelProps & { field: string; label: string }) {
  const contactsQuery = useQuery({
    queryKey: ['support-contacts', tenantId],
    queryFn: ({ signal }) => fetchSupportContacts({ tour_operator_id: tenantId as number }, signal),
    staleTime: 5 * 60_000,
    enabled: tenantId != null,
  });

  if (tenantId == null) {
    return (
      <TextField
        size="small"
        sx={{ width: 280 }}
        label={label}
        value=""
        disabled
        helperText="System templates have no tenant — support contacts are unavailable."
      />
    );
  }

  const options = contactsQuery.data?.results ?? [];
  const currentId = idOf(dm, field);
  const value =
    options.find((c) => c.id === currentId) ??
    (currentId != null
      ? ({
          id: currentId,
          name: `#${currentId}`,
          contact: null,
          contact_type: 0,
          enabled: true,
        } as SupportContact)
      : null);

  return (
    <Box sx={{ width: 280 }}>
      <Autocomplete<SupportContact>
        size="small"
        options={options}
        value={value}
        disabled={!canManage}
        loading={contactsQuery.isLoading}
        getOptionLabel={(c) =>
          c.contact ? `${c.name ?? c.contact} (${c.contact})` : (c.name ?? `#${c.id}`)
        }
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, v) => onFieldChange(dm.id, field, v?.id ?? null)}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
      {value?.contact && (
        <PickerLinks
          items={[
            {
              key: value.id,
              label: value.contact,
              // The contact is an address or a number depending on the channel
              // the picker sits on — let the value decide.
              actions: contactActions(
                value.contact,
                value.contact.includes('@') ? 'email' : 'tel',
              ),
            },
          ]}
        />
      )}
    </Box>
  );
}

/** Chat origin user — tenant-scoped console user lookup (`/users/`). */
interface UserOption {
  id: number;
  label: string;
}

function ChatOriginUserField({ dm, tenantId, canManage, onFieldChange }: PanelProps) {
  const currentId = idOf(dm, 'chat_origin_user');
  // Label the persisted value (the search only returns matching pages).
  const currentUserQuery = useQuery({
    queryKey: ['user-brief', currentId],
    queryFn: () => usersApi.retrieve(currentId as number),
    staleTime: 5 * 60_000,
    enabled: currentId != null,
  });

  if (tenantId == null) {
    return (
      <TextField
        size="small"
        sx={{ width: 280 }}
        label="Chat origin user"
        value=""
        disabled
        helperText="System templates have no tenant — user lookup is unavailable."
      />
    );
  }

  const toOption = (u: usersApi.ConsoleUserListItem | usersApi.ConsoleUserDetail): UserOption => ({
    id: u.id,
    label:
      [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.login || `#${u.id}`,
  });

  const value: UserOption | null =
    currentId == null
      ? null
      : currentUserQuery.data
        ? toOption(currentUserQuery.data)
        : { id: currentId, label: `#${currentId}` };

  return (
    <Box sx={{ width: 280 }}>
      <RemoteAutocomplete<UserOption>
        size="small"
        label="Chat origin user"
        queryFn={(q) =>
          usersApi
            .list({ tour_operator: tenantId, search: q, pageSize: 20 })
            .then((r) => r.results.map(toOption))
        }
        getOptionLabel={(u) => u.label}
        getOptionKey={(u) => u.id}
        value={value}
        onChange={(v) => {
          if (!canManage) return;
          onFieldChange(dm.id, 'chat_origin_user', (v as UserOption | null)?.id ?? null);
        }}
        fetchOnEmpty
      />
      {value && (
        <PickerLinks
          items={[
            {
              key: value.id,
              label: value.label,
              actions: userActions({
                id: value.id,
                email: currentUserQuery.data?.email ?? null,
              }),
            },
          ]}
        />
      )}
    </Box>
  );
}

/** JSON field over JsonEditorWidget. The widget is imperative (no change
 *  events; value read via ref), so edits are committed with an explicit
 *  "Apply" button that parses and writes into the draft. */
function JsonField({
  dm,
  canManage,
  onFieldChange,
  field,
  label,
}: FieldProps & { field: string; label: string }) {
  const editorRef = useRef<JsonEditorHandle>(null);
  const [error, setError] = useState<string | null>(null);
  const initial = dm[field];
  // JSONField columns may hold any JSON value (arrays included) — pass
  // objects/arrays through; scalars/null start the editor empty.
  const initialValue =
    initial !== null && typeof initial === 'object' ? (initial as Record<string, unknown>) : null;

  const apply = () => {
    try {
      const value = editorRef.current?.getValue() ?? {};
      setError(null);
      onFieldChange(dm.id, field, value);
    } catch {
      setError('Invalid JSON — fix the content before applying.');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{label}</Typography>
        {canManage && (
          <Button size="small" startIcon={<CheckIcon />} onClick={apply}>
            Apply JSON
          </Button>
        )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>
      <Box sx={{ height: 220 }}>
        {/* Key by dm+field: the widget initializes once and never re-inits. */}
        <JsonEditorWidget
          key={`${dm.id}-${field}`}
          ref={editorRef}
          initialValue={initialValue}
          readOnly={!canManage}
          mode={canManage ? 'code' : 'view'}
        />
      </Box>
    </Box>
  );
}

/** AWS-key style secret: the server masks stored secrets as '•••' (the
 *  SECRET_SENTINEL) and its write funnel treats the sentinel as "unchanged".
 *  Render a password field carrying the mask; only a real keystroke writes
 *  into the draft — a real secret is never displayed or re-sent. */
function SecretField({
  dm,
  canManage,
  onFieldChange,
  field,
  label,
}: FieldProps & { field: string; label: string }) {
  return (
    <TextField
      size="small"
      sx={{ width: 280 }}
      label={label}
      type="password"
      autoComplete="new-password"
      value={strOf(dm, field)}
      disabled={!canManage}
      helperText="Stored value is masked (•••). Type to replace it; clear to remove."
      onChange={(e) => onFieldChange(dm.id, field, e.target.value)}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-channel sections
// ---------------------------------------------------------------------------

function DestinationSection(props: PanelProps) {
  const { dm } = props;
  const spec = DESTINATION_CHANNELS[dm.method];
  if (!spec) return null;
  const destination = numOr(dm, spec.destinationField, 0);
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
      <EnumSelect
        {...props}
        field={spec.destinationField}
        label="Destination"
        labels={spec.destinationLabels}
      />
      {destination === DEST_SUPPORT_CONTACT && (
        <SupportContactField {...props} field={spec.supportField} label={spec.supportLabel} />
      )}
      {destination === DEST_ARBITRARY && (
        <TextInput
          {...props}
          field={spec.addressField}
          label={spec.addressLabel}
          actionKind={dm.method === DELIVERY_METHODS.EMAIL ? 'email' : 'tel'}
        />
      )}
      <FlagSwitch {...props} field={spec.allowOverrideField} label={spec.allowOverrideLabel} />
    </Stack>
  );
}

function PreventRepliesSection(props: FieldProps) {
  const { dm } = props;
  return (
    <Stack spacing={1}>
      <FlagSwitch
        {...props}
        field="prevent_replies"
        label="Prevent replies (one-way message)"
        hint="Replies are not routed into chat — the sender receives the auto-response below instead."
      />
      {boolOf(dm, 'prevent_replies') && (
        <TextInput
          {...props}
          field="prevent_replies_message"
          label="Auto-response when someone replies"
          width="100%"
          multiline
          helperText="Leave blank to use the default message."
        />
      )}
    </Stack>
  );
}

function EmailSection(props: PanelProps) {
  return (
    <Stack spacing={1.5}>
      <DestinationSection {...props} />
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextInput {...props} field="email_replyto" label="Reply-to" actionKind="email" />
        <FlagSwitch
          {...props}
          field="email_append_conversation_sid"
          label="Append conversation SID"
          hint="Append the chat conversation SID to the email so replies land in the conversation."
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
          Attachments
        </Typography>
        {EMAIL_ATTACH_FIELDS.map((f) => (
          <FlagSwitch key={f.field} {...props} field={f.field} label={f.label} hint={f.hint} />
        ))}
      </Stack>
      <PreventRepliesSection {...props} />
    </Stack>
  );
}

function SmsLikeSection(props: PanelProps) {
  return (
    <Stack spacing={1.5}>
      <DestinationSection {...props} />
      <PreventRepliesSection {...props} />
    </Stack>
  );
}

function ChatSection(props: PanelProps) {
  const { dm } = props;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <EnumSelect
          {...props}
          field="chat_destination"
          label="Destination"
          labels={CHAT_DESTINATION_LABELS}
        />
        {numOr(dm, 'chat_destination', 0) === CHAT_TO_ARBITRARY_SID && (
          <TextInput
            {...props}
            field="chat_conversation_sid"
            label="Arbitrary conversation SID"
            actionKind="copy"
          />
        )}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <EnumSelect
          {...props}
          field="chat_origin"
          label="Message sender"
          labels={CHAT_ORIGIN_LABELS}
        />
        {numOr(dm, 'chat_origin', 0) === CHAT_ORIGIN_USER && <ChatOriginUserField {...props} />}
      </Stack>
    </Stack>
  );
}

function AirbnbSection(props: PanelProps) {
  const { dm } = props;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <EnumSelect
          {...props}
          field="airbnb_destination"
          label="Destination"
          labels={AIRBNB_DESTINATION_LABELS}
        />
        {numOr(dm, 'airbnb_destination', 0) === AIRBNB_TO_ARBITRARY_THREAD && (
          <TextInput {...props} field="airbnb_thread_id" label="Airbnb thread ID" />
        )}
      </Stack>
      <PreventRepliesSection {...props} />
    </Stack>
  );
}

function HttpSection(props: PanelProps) {
  const { dm, canManage, onFieldChange } = props;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          sx={{ width: 140 }}
          label="Method"
          value={strOf(dm, 'http_method')}
          disabled={!canManage}
          onChange={(e) => onFieldChange(dm.id, 'http_method', e.target.value || null)}
        >
          {HTTP_METHODS.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
        <TextInput {...props} field="http_url" label="Target URL" width={420} actionKind="url" />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TextInput
          {...props}
          field="http_response_status"
          label="Expected response status"
          helperText="Comma-separated to accept multiple."
        />
        <TextField
          size="small"
          sx={{ width: 180 }}
          label="Retry count"
          type="number"
          value={typeof dm.http_retry_count === 'number' ? dm.http_retry_count : ''}
          disabled={!canManage}
          helperText="Blank = default (5)."
          onChange={(e) =>
            onFieldChange(
              dm.id,
              'http_retry_count',
              e.target.value === '' ? null : Number(e.target.value),
            )
          }
        />
      </Stack>
      <JsonField {...props} field="http_headers" label="HTTP headers (JSON)" />
      <JsonField
        {...props}
        field="http_data"
        label="HTTP request data (JSON — template variables allowed)"
      />
    </Stack>
  );
}

function SnsSection(props: PanelProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
      <TextInput {...props} field="sns_topic" label="SNS topic ARN" width={420} actionKind="copy" />
      <SecretField {...props} field="sns_aws_access_key" label="AWS access key" />
      <SecretField {...props} field="sns_aws_secret_key" label="AWS secret key" />
    </Stack>
  );
}

function SentrySection(props: PanelProps) {
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TextInput {...props} field="sentry_tag_key" label="Sentry tag key" />
        <TextInput {...props} field="sentry_tag_value" label="Sentry tag value" />
      </Stack>
      <JsonField
        {...props}
        field="sentry_context"
        label="Sentry context (JSON — template variables allowed)"
      />
      <TextInput
        {...props}
        field="sentry_extra"
        label="Sentry extra"
        width="100%"
        multiline
        helperText="Template variables can be used."
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

export function ChannelSettingsPanel(props: PanelProps) {
  switch (props.dm.method) {
    case DELIVERY_METHODS.EMAIL:
      return <EmailSection {...props} />;
    case DELIVERY_METHODS.SMS:
    case DELIVERY_METHODS.WHATSAPP:
      return <SmsLikeSection {...props} />;
    case DELIVERY_METHODS.CHAT:
      return <ChatSection {...props} />;
    case DELIVERY_METHODS.AIRBNB:
      return <AirbnbSection {...props} />;
    case DELIVERY_METHODS.HTTP_REQUEST:
      return <HttpSection {...props} />;
    case DELIVERY_METHODS.SNS_TOPIC:
      return <SnsSection {...props} />;
    case DELIVERY_METHODS.SENTRY:
      return <SentrySection {...props} />;
    default:
      // Push has no console-editable settings.
      return null;
  }
}
